import GoogleMap, { ICircle, ICoordinate, SinglePointCoordinateData } from "components/GoogleMap/googlemap";
import React, { forwardRef, useImperativeHandle } from "react";
import { toMeters } from "utilities/geometry";
import { bounce, bounceOnce, useHoverMarker, useSelectMarker } from "utilities/googlemaps";

interface Props {
  activeShow: server.dto.Show
  activeShowClause: server.dto.ExclusivityClause
  conflictingShows: server.dto.ConflictingShow[]

  hoveredLocation: server.dto.Geo
  onHoveredLocationChange: (location: server.dto.Geo, showId: number) => void

  selectedLocation: server.dto.Geo
  selectedClause: server.dto.ExclusivityClause
  onSelectedLocationChange: (location: server.dto.Geo, showId: number, clause: server.dto.ExclusivityClause) => void

  onMapRendered: () => void
}

export interface GoogleMapConflictingShowsRef {
  bounceMarkerOnce: (location: server.dto.Geo) => void;
}

export const GoogleMapConflictingShows = forwardRef((props: Props, ref) => {
  const selectMarker = useSelectMarker();
  const onMarkerHover = useHoverMarker(props.selectedLocation);

  const mapRef = React.useRef<GoogleMap>(null);

  const bounceMarkerOnce = (location: server.dto.Geo) => {
    const marker = mapRef.current.getMarkerFromCoordinate({
      position: { lat: location.lat, lng: location.lng },
    });

    bounceOnce(marker);
  }

  useImperativeHandle(ref, () => ({
    bounceMarkerOnce,
  }));

  React.useEffect(() => {
    if (props.selectedLocation) {
      const markerThatShouldBeSelected = mapRef.current.getMarkerFromCoordinate({
        position: { lat: props.selectedLocation.lat, lng: props.selectedLocation.lng },
      });

      selectMarker(markerThatShouldBeSelected);
      bounce(markerThatShouldBeSelected);
    }
  }, [props.selectedLocation]);

  React.useEffect(() => {
    let hoveredGoogleMarker: google.maps.marker.AdvancedMarkerElement = undefined;
    if (props.hoveredLocation) {
      hoveredGoogleMarker = mapRef.current.getMarkerFromCoordinate({
        position: { lat: props.hoveredLocation.lat, lng: props.hoveredLocation.lng },
      });
    }

    onMarkerHover(hoveredGoogleMarker);
  }, [props.hoveredLocation]);

  const activeShowCoordinate: ICoordinate = {
    clickable: true,
    position: {
      lat: props.activeShow.venueGeoLocation.lat,
      lng: props.activeShow.venueGeoLocation.lng,
    },
    data: SinglePointCoordinateData,
    pin: {
      type: "show",
      phase: props.activeShow.status.appointmentPhase,
    },

    onLoaded: (_, marker) => {
      google.maps.event.addListener(marker, "click", () => {
        props.onSelectedLocationChange(props.activeShow.venueGeoLocation, props.activeShow.showId, props.activeShowClause);
      });

      marker.content.addEventListener("mouseenter", () => {
        props.onHoveredLocationChange(props.activeShow.venueGeoLocation, props.activeShow.showId);
      });

      marker.content.addEventListener("mouseleave", () => {
        props.onHoveredLocationChange(undefined, -1);
      });
    }
  }
  const conflictingShowsCoordinates: ICoordinate[] = props.conflictingShows.map(conflict => ({
    clickable: true,
    position: {
      lat: conflict.venue.location.lat,
      lng: conflict.venue.location.lng,
    },
    data: SinglePointCoordinateData,
    pin: {
      type: "show",
      phase: conflict.appointmentPhase,
    },
    onLoaded: (_, marker) => {
      google.maps.event.addListener(marker, "click", () => {
        props.onSelectedLocationChange(conflict.venue.location, conflict.showId, conflict.exclusivityClause);
      });

      marker.content.addEventListener("mouseenter", () => {
        props.onHoveredLocationChange(conflict.venue.location, conflict.showId);
      });

      marker.content.addEventListener("mouseleave", () => {
        props.onHoveredLocationChange(undefined, -1);
      });
    }
  }));
  const coordinates = [activeShowCoordinate, ...conflictingShowsCoordinates];

  const circles: ICircle[] = props.selectedClause ? [{
    id: `exclusivityClause-${props.selectedLocation.lat}-${props.selectedLocation.lng}`,
    center: props.selectedLocation,
    radiusMeters: toMeters(props.selectedClause.radius, props.selectedClause.unitOfDistance),
  }] : [];

  return <div style={{
    height: "100%",
    borderRadius: "var(--radius-large)",
    overflow: "hidden",
  }}>
    <GoogleMap
      ref={mapRef}
      mapId="DEMO_ID"
      autoFitBounds
      coordinates={coordinates}
      circles={circles}
      mapOptions={{
        fullscreenControl: false,
        streetViewControl: false,
        mapTypeControl: true,
        mapTypeControlOptions: {
          style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
        },
        center: activeShowCoordinate.position,
        zoomControlOptions: {
          style: google.maps.ZoomControlStyle.SMALL,
        },
        scrollwheel: true
      }}
      onLoaded={map => {
        google.maps.event.addListener(map, "mouseout", () => {
          map.setOptions({
            scrollwheel: false
          });
        });

        google.maps.event.addListener(map, "mouseover", () => {
          map.setOptions({
            scrollwheel: true
          });
        });

        // NOTE: This is kindof a hack, that only works because we are auto-fitting the bounds.
        //  The map unfortunately doesn't have an on load event
        google.maps.event.addListenerOnce(map, "center_changed", () => {
          props.onMapRendered();
        });
      }}
    />
  </div>
});